/**
 * MEXC UID 인증 (회원 앱 · 보상 탭)
 *
 * 배너를 누르면 MEXC 가입 페이지로 가지만, 가입만으로는 지급되지 않는다.
 * 이 화면에서 UID 를 입력해야 제휴 API 로 확인하고 지급한다.
 *
 * UID 는 그냥 숫자라 아무거나 적어낼 수 있다. 서버가 제휴 API 로 조회해
 * 우리 코드로 가입한 것인지 확인하므로, 화면에서는 형식만 걸러준다.
 */
import { useState, useEffect } from 'react';
import { CheckCircle2, AlertTriangle, ExternalLink, Gift, Lock } from 'lucide-react';
import { toast } from 'sonner';

interface Props { authHeaders: () => Record<string, string>; }

const SIGNUP_URL = 'https://www.mexc.com/acquisition/custom-sign-up?shareCode=mexc-handy';

export function MexcClaim({ authHeaders }: Props) {
  const [st, setSt] = useState<any>(null);
  const [uid, setUid] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const d = await fetch('/api/mexc/status', { headers: authHeaders() }).then(r => r.json());
      if (d.success) setSt(d);
    } catch { /* 조회 실패가 화면을 막지 않는다 */ }
  };
  useEffect(() => { load(); }, []);

  if (!st || !st.enabled) return null;   // 이벤트가 닫혀 있으면 아예 안 보인다

  const claim = st.claim;
  const lock = st.lock?.enabled && st.lock?.cliffMonths > 0;

  const submit = async () => {
    const v = uid.trim();
    if (!/^\d{5,15}$/.test(v)) { toast.error('UID 는 숫자만 입력해주세요.'); return; }
    if (!window.confirm(
      `MEXC UID ${v} 로 신청합니다.\n\n`
      + `받을 수량: ${Number(st.reward).toLocaleString()} wHANDY\n`
      + (lock ? `락업: ${st.lock.cliffMonths}개월 거치 · ${st.lock.splitCount}회 분할\n` : '')
      + `\n한 번 등록한 UID 는 바꿀 수 없습니다. 진행할까요?`)) return;

    setBusy(true);
    try {
      const d = await fetch('/api/mexc/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ uid: v }),
      }).then(r => r.json());
      if (!d.success) { toast.error(d.error || '확인 실패'); load(); return; }
      toast.success(d.status === 'completed'
        ? `${Number(d.amount).toLocaleString()} wHANDY 지급 완료`
        : '확인되었습니다. 곧 지급됩니다.');
      setUid('');
      load();
    } catch (e: any) { toast.error(e?.message || '오류'); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Gift className="w-5 h-5" style={{ color: '#FF6B00' }} />
        <h3 className="font-bold">MEXC 가입 이벤트</h3>
      </div>

      {/* ── 이미 받음 ── */}
      {claim?.status === 'completed' && (
        <div className="rounded-lg bg-green-50 border border-green-200 p-3">
          <p className="font-bold text-green-800 flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> 지급 완료
          </p>
          <p className="text-sm text-green-700 mt-1">
            UID {claim.mexc_uid} · {Number(claim.amount).toLocaleString()} wHANDY
          </p>
          {claim.lock_applied && claim.lock_start_date && (
            <p className="text-xs text-green-700 mt-0.5">
              {String(claim.lock_start_date).slice(0, 10)} 부터 {claim.lock_split_count}회에 걸쳐 해제
            </p>
          )}
        </div>
      )}

      {/* ── 확인됨, 지급 대기 ── */}
      {(claim?.status === 'pending' || claim?.status === 'processing') && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
          <p className="font-bold text-amber-900">확인되었습니다</p>
          <p className="text-sm text-amber-800 mt-1">
            UID {claim.mexc_uid} · 곧 {Number(claim.amount || st.reward).toLocaleString()} wHANDY 가 지급됩니다.
          </p>
        </div>
      )}

      {/* ── 실패/거절 ── */}
      {(claim?.status === 'rejected' || claim?.status === 'failed') && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3">
          <p className="font-bold text-red-700 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" /> 확인되지 않았습니다
          </p>
          <p className="text-sm text-red-600 mt-1">
            {claim.reject_reason || claim.error_message || '다시 시도해주세요.'}
          </p>
        </div>
      )}

      {/* ── 아직 신청 전 / 재시도 가능 ── */}
      {(!claim || claim.status === 'rejected' || claim.status === 'failed') && (
        <>
          <p className="text-sm text-muted-foreground">
            아래 링크로 MEXC 에 가입한 뒤, 그 계정의 <b>UID</b> 를 입력하면{' '}
            <b>{Number(st.reward).toLocaleString()} wHANDY</b> 를 드립니다.
          </p>

          <a href={SIGNUP_URL} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-center gap-1.5 w-full py-3 rounded-xl font-bold text-white"
            style={{ background: '#FF6B00' }}>
            MEXC 가입하러 가기 <ExternalLink className="w-4 h-4" />
          </a>

          <div>
            <label className="text-sm font-semibold">MEXC UID</label>
            <input
              value={uid}
              onChange={e => setUid(e.target.value.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              placeholder="예: 86343689"
              className="w-full mt-1 px-4 py-3.5 rounded-xl text-lg outline-none border-2 border-border bg-white text-black"
            />
            <p className="text-xs text-muted-foreground mt-1">
              MEXC 앱 → 마이페이지 상단에서 확인할 수 있습니다.
            </p>
          </div>

          {lock && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
              <p className="text-sm text-amber-900 flex items-start gap-1.5">
                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  받은 wHANDY 는 <b>{st.lock.cliffMonths}개월간 사용할 수 없고</b>,{' '}
                  {st.lock.cliffMonths + 1}개월차부터 매월{' '}
                  {Math.round(100 / st.lock.splitCount)}%씩 {st.lock.splitCount}회에 걸쳐 풀립니다.
                </span>
              </p>
            </div>
          )}

          {st.requireDeposit && (
            <p className="text-xs" style={{ color: '#B45309' }}>
              MEXC 에서 첫 입금까지 완료해야 지급됩니다.
            </p>
          )}

          <button
            onClick={submit}
            disabled={busy || !/^\d{5,15}$/.test(uid.trim())}
            className="w-full py-4 rounded-xl font-bold text-lg text-white disabled:opacity-40"
            style={{ background: '#FF6B00' }}>
            {busy ? '확인 중…' : 'UID 인증하고 받기'}
          </button>

          <p className="text-xs text-muted-foreground">
            우리 링크로 가입한 UID 만 인정됩니다. 한 UID 는 한 번만 받을 수 있습니다.
          </p>
        </>
      )}
    </div>
  );
}
